import React from "react";
import RtrToggle from "components/design-system/rtr-toggle";
import QuestionMark from "images/sprites/reb/svg/question-mark.svg";
import Tooltip from "components/design-system/tooltip";
import classNames from "classnames";
import { useOnLoadFlagOrExperiment } from "../hoc/with-flags-and-experiments";
import flagsAndExperimentsNames from "constants/flags-and-experiments-names";
import { isViewingAvailableOnly } from "helpers/grid-migration-helpers";
import { useAppDispatch, useAppSelector, type RootState } from "../../../../../util/store-types";
import { useAvailabilityFilters } from "hooks/use-availability-filters";
import { openGridAvailabilityModal, toggleGridFilterModal } from "actions/grid-modal-actions";
import RentalDatePill from "./rental-dates-pill";
import { getRentBeginDate } from "helpers/membership-helpers";
import { parseISOWithoutTime } from "helpers/date-helpers";
import { format, isToday, isTomorrow } from "date-fns";
import { dateFnsFormats } from "rtr-constants";

export type GridAvailabilityToggleProps = {
  isComingSoonGrid?: boolean;
  isShortlistGrid?: boolean;
  onSubmitFilters: () => void;
};

const GridAvailabilityToggle = ({
  isComingSoonGrid,
  isShortlistGrid,
  onSubmitFilters,
}: GridAvailabilityToggleProps) => {
  const rentalBeginDateSelector = ({ membershipRentalBeginDate, membershipState }: RootState) => {
    const rentalBegin = getRentBeginDate(membershipState) ?? membershipRentalBeginDate;

    if (!rentalBegin) return;

    const parsed = parseISOWithoutTime(rentalBegin);

    if (isToday(parsed)) {
      return "Today";
    } else if (isTomorrow(parsed)) {
      return "Tomorrow";
    } else return format(parsed, dateFnsFormats.MMM_D);
  };

  const workingFilters = useAppSelector(state => state.workingFilters) || {};
  const isClassicLens = useAppSelector(state => state.userData?.isClassicLens);
  const membershipState = useAppSelector(state => state.membershipState);
  const isMobileViewport = useAppSelector(state => state.browser?.isMobileViewport);
  const membershipRentalBeginDate = useAppSelector(rentalBeginDateSelector);
  const [reserveAsFilterFlag] = useOnLoadFlagOrExperiment(flagsAndExperimentsNames.RESERVE_FILTER);

  const { setOrToggleMembershipAvailability } = useAvailabilityFilters(onSubmitFilters);
  const dispatch = useAppDispatch();

  const [availableNowToggleFlag] = useOnLoadFlagOrExperiment(
    flagsAndExperimentsNames.PROSPECTS_295_ENABLE_GRID_AVAILABLE_NOW_TOGGLE_COPY_TEST
  );
  const isViewingAvailableProducts = () => {
    return isViewingAvailableOnly(workingFilters, membershipState);
  };

  const getInnerTooltipContent = () => {
    if (isComingSoonGrid)
      return <div className="mb-3">This toggle is turned off until these styles are ready to rent</div>;
    if (isClassicLens)
      return (
        <>
          <div className="mb-3">Browse with subscription to view items available now.</div>
          <button onClick={setOrToggleMembershipAvailability} className="underline">
            Browse Subscription
          </button>
        </>
      );
    return (
      <>
        <div className="mb-3">Toggle on to display items currently available to rent with subscription.</div>
        <a href="/clothing-subscription" className="underline">
          How It Works
        </a>
      </>
    );
  };

  const renderText = (checked: boolean) => {
    if (!availableNowToggleFlag) return "Available Now";
    if (checked) return "In-Stock Only";
    return isClassicLens ? (
      <button
        onClick={() => dispatch(openGridAvailabilityModal())}
        className="underline"
        data-test-id="grid-availability-modal-open">
        Browse Subscription
      </button>
    ) : (
      "All Items"
    );
  };

  const tooltipAnchor = <QuestionMark />;

  const tooltipContent = (
    <div className="text-black p-4 w-72 flex flex-col align-center text-center">{getInnerTooltipContent()}</div>
  );

  const classes = classNames("flex", "items-center", "justify-start", {
    "basis-1/2": !reserveAsFilterFlag,
    "md:basis-1/3": !isShortlistGrid && !reserveAsFilterFlag,
    "basis-[300px]": reserveAsFilterFlag,
  });

  const checked = (isViewingAvailableProducts() && !isClassicLens) ?? false;
  const heapId = checked ? "grid-availability-toggle-enabled" : "grid-availability-toggle-disabled";

  const openFilterModal = () => dispatch(toggleGridFilterModal(true));

  const renderAvailabilityTypeCopy = () => {
    const textClasses = "font-medium underline";

    if (isComingSoonGrid) {
      return <span className={classNames(textClasses, "cursor-default")}>Coming Soon</span>;
    } else if (isClassicLens && workingFilters.date) {
      return <RentalDatePill onClearDates={setOrToggleMembershipAvailability} onClick={openFilterModal} />;
    } else if (isViewingAvailableProducts()) {
      return (
        <button className={textClasses} onClick={openFilterModal}>
          In-Stock Only
        </button>
      );
    } else {
      return (
        <button className={textClasses} onClick={openFilterModal}>
          All Items
        </button>
      );
    }
  };

  const renderReserveAsFilterFlagOn = () => {
    return (
      <div>
        <div className="flex items-center gap-1">
          <p className="font-normal">Browsing: </p>
          {renderAvailabilityTypeCopy()}
        </div>
        {isViewingAvailableProducts() && membershipRentalBeginDate && (
          <p className="font-normal">Arrives {membershipRentalBeginDate}</p>
        )}
      </div>
    );
  };

  const renderReserveAsFilterFlagOff = () => {
    return (
      <>
        {availableNowToggleFlag && isClassicLens && isMobileViewport ? (
          <span className="normal-case text-sm mr-2">{renderText(false)}</span>
        ) : (
          <RtrToggle
            labelText={renderText(checked)}
            checked={checked}
            disabled={isClassicLens || isComingSoonGrid || false}
            dataHeapId={heapId}
            dataTestId="grid-availability-toggle"
            onChange={setOrToggleMembershipAvailability}
          />
        )}
        <div>
          <Tooltip
            anchorElement={tooltipAnchor}
            position="top"
            tooltipContent={tooltipContent}
            tooltipClassName={isMobileViewport ? "ml-6" : ""}
            arrowClassName={isMobileViewport ? "-ml-6" : ""}
            className=""
          />
        </div>
      </>
    );
  };

  return (
    <div className={classes}>
      {reserveAsFilterFlag ? renderReserveAsFilterFlagOn() : renderReserveAsFilterFlagOff()}
    </div>
  );
};

export default GridAvailabilityToggle;
